<template>
  <section class="section is-medium bg-gradient-process">
    <div class="container">
      <div class="has-text-centered mb-6">
        <h2 class="title is-2 has-line animate fade-in-up">
          {{ $t('landing.how-it-works-title') }}
        </h2>
      </div>
      
      <div class="process-container">
        <div class="process-path" />
        
        <div class="columns is-multiline">
          <div
            v-for="(step, index) in steps"
            :key="index" 
            class="column is-3 process-step"
          >
            <div
              class="process-card animate"
              :class="getAnimationClass(index)"
            >
              <div
                class="process-number"
                :class="step.colorClass"
              >
                {{ index + 1 }}
              </div>
              <h3 class="process-title">
                {{ $t(step.title) }}
              </h3>
              <p class="process-description">
                {{ $t(step.description) }}
              </p>
              <div
                class="process-icon"
                :class="step.colorClass"
              >
                <i :class="step.icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const steps = [
  {
    title: 'landing.how-it-works-step1-title',
    description: 'landing.how-it-works-step1-description',
    icon: 'fas fa-cloud-upload-alt',
    colorClass: 'is-indiefy-blue',
  },
  {
    title: 'landing.how-it-works-step2-title',
    description: 'landing.how-it-works-step2-description',
    icon: 'fas fa-music',
    colorClass: 'is-indiefy-teal',
  },
  {
    title: 'landing.how-it-works-step3-title',
    description: 'landing.how-it-works-step3-description',
    icon: 'fas fa-rocket',
    colorClass: 'is-indiefy-pink',
  },
  {
    title: 'landing.how-it-works-step4-title',
    description: 'landing.how-it-works-step4-description',
    icon: 'fas fa-chart-line',
    colorClass: 'is-indiefy-lavender',
  },
];

const getAnimationClass = (index) => {
  const delay = index > 0 ? ` animate--delay-${index}` : '';
  return `fade-in-up${delay}`;
};
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/animations";

.bg-gradient-process {
  background: linear-gradient(135deg, 
    rgba(lighten($indiefy-teal, 35%), 0.5) 0%, 
    rgba(lighten($indiefy-blue, 25%), 0.5) 100%
  );
  
  .body--dark & {
    background: linear-gradient(135deg, 
      rgba(darken($indiefy-teal, 15%), 0.3) 0%, 
      rgba(darken($indiefy-blue, 10%), 0.3) 100%
    );
  }
}

.title.has-line {
  position: relative;
  padding-bottom: 1.5rem;
  color: var(--color-text, #000);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
    border-radius: 2px;
  }
}

.process-container {
  position: relative;
  padding: 2rem 0;
}

.process-path {
  position: absolute;
  top: 120px;
  left: 8%;
  width: 84%;
  height: 4px;
  background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
  border-radius: 2px;
  z-index: 0;
  
  @media screen and (max-width: 768px) {
    display: none;
  }
}

.process-step {
  position: relative;
  z-index: 1;
}

.process-card {
  background-color: var(--color-background, white);
  border-radius: 12px;
  padding: 2rem 1.5rem;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  height: 100%;
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
}

.process-number {
  width: 50px;
  height: 50px;
  line-height: 50px;
  border-radius: 25px;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  &.is-indiefy-blue {
    background-color: $indiefy-blue;
  }
  
  &.is-indiefy-teal {
    background-color: $indiefy-teal;
  }
  
  &.is-indiefy-pink {
    background-color: $indiefy-pink;
  }
  
  &.is-indiefy-lavender {
    background-color: $indiefy-lavender;
  }
}

.process-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--color-text, #000);
}

.process-description {
  font-size: 0.95rem;
  color: var(--color-text-light, #666);
  margin-bottom: 1.5rem;
}

.process-icon {
  font-size: 2rem;
  margin-top: auto;
  
  &.is-indiefy-blue {
    color: $indiefy-blue;
  }
  
  &.is-indiefy-teal {
    color: $indiefy-teal;
  }
  
  &.is-indiefy-pink {
    color: $indiefy-pink;
  }
  
  &.is-indiefy-lavender {
    color: $indiefy-lavender;
  }
}

@media screen and (max-width: 768px) {
  .process-card {
    margin-bottom: 2rem;
  }
}
</style>